import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Trigger file download from blob
 */
const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Export data to CSV format
 * @param {Array<Object>} data - Array of row objects
 * @param {string} filename - File name without extension
 */
export const exportToCSV = (data, filename = 'report') => {
  if (!data || data.length === 0) {
    alert('No data to export.');
    return;
  }
  const headers = Object.keys(data[0]);
  const csvRows = [
    headers.join(','),
    ...data.map(row => headers.map(h => {
      const val = row[h];
      const str = val == null ? '' : String(val);
      return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"` : str;
    }).join(','))
  ];
  const csv = csvRows.join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
  downloadBlob(blob, `${filename}.csv`);
};

/**
 * Export data to XLSX format
 * @param {Array<Object>} data - Array of row objects
 * @param {string} filename - File name without extension
 * @param {string} sheetName - Worksheet name
 */
export const exportToXLSX = (data, filename = 'report', sheetName = 'Report') => {
  if (!data || data.length === 0) {
    alert('No data to export.');
    return;
  }
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.substring(0, 31));
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

/**
 * Export data to XLS format (Excel 97-2003)
 * @param {Array<Object>} data - Array of row objects
 * @param {string} filename - File name without extension
 * @param {string} sheetName - Worksheet name
 */
export const exportToXLS = (data, filename = 'report', sheetName = 'Report') => {
  if (!data || data.length === 0) {
    alert('No data to export.');
    return;
  }
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.substring(0, 31));
  XLSX.writeFile(wb, `${filename}.xls`, { bookType: 'xls' });
};

/**
 * Bank of Baroda NEFT/RTGS upload layout:
 * Company name, batch/cheque line, then SN | Debit A/C | Amount | IFSC | Beneficiary A/C | Name | A/C Type, then TOTAL.
 */
export const exportNeftRtgsXlsx = ({
  companyName = 'Yelooo',
  batchLabel = '',
  debitAccount = '',
  rows = [],
  filename = 'neft-rtgs-points-payout',
} = {}) => {
  if (!rows.length) {
    alert('No customers with bank details and unpaid points to export.');
    return;
  }
  const dataRows = rows.map((row, index) => ([
    index + 1,
    debitAccount,
    Number(row.amount) || 0,
    row.ifscCode || '',
    row.accountNumber || '',
    row.beneficiaryName || '',
    row.accountType || 'SA',
    row.customerCode || '',
    row.aadharNumber || '',
    row.panNumber || '',
    row.gstNumber || '',
  ]));
  const total = dataRows.reduce((sum, row) => sum + Number(row[2] || 0), 0);
  const aoa = [
    [companyName],
    [batchLabel],
    ['SN', 'Debit A/C Number', 'Amount', 'IFSC Code', 'Beneficiary A/C Number', 'BeneFiciary Name', 'A/C Type', 'Customer Code', 'Aadhar Number', 'PAN Number', 'GST Number'],
    ...dataRows,
    [],
    ['', 'TOTAL', total],
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [
    { wch: 8 },
    { wch: 22 },
    { wch: 14 },
    { wch: 16 },
    { wch: 24 },
    { wch: 32 },
    { wch: 12 },
    { wch: 16 },
    { wch: 18 },
    { wch: 16 },
    { wch: 18 },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'NEFT RTGS');
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

/**
 * Export data to PDF format (table)
 * @param {Array<Object>} data - Array of row objects
 * @param {string} filename - File name without extension
 * @param {string} title - Report title
 */
export const exportToPDF = (data, filename = 'report', title = 'Report') => {
  if (!data || data.length === 0) {
    alert('No data to export.');
    return;
  }
  const headers = Object.keys(data[0]);
  const rows = data.map(row => headers.map(h => row[h] ?? ''));
  const doc = new jsPDF({ orientation: headers.length > 6 ? 'landscape' : 'portrait' });
  doc.setFontSize(14);
  doc.text(title, 14, 15);
  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: 22,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [66, 139, 202] },
  });
  doc.save(`${filename}.pdf`);
};
